import Icon from '@/components/snippets/icon/icon'
import { useCallback, useMemo, useState } from 'preact/hooks'
import { AccountWishlistModal } from '../../omneo/components/AccountWishlistModal'
import { AccountKidsSizeSuggestion } from '../../omneo/components/AccountKidSizeSuggestion'
import { PageModal } from '../../omneo/components/AccountModal'
import { getAllWishlists, removeProductFromAllWishlists } from '../../omneo/helpers/getAllWishlists'
import { useOmneoClient } from '../../omneo/hooks/useOmneoClient'
import { pushWishlistSaveEvent } from '@/components/snippets/datalayer/bc-wishlist-datalayer'

const VIEW_STATES = {
  SELECT: 'SELECT',
  ADD_TO_WISHLIST: 'ADD_TO_WISHLIST',
  NOTIFY_ME: 'NOTIFY_ME'
}

export const WsIcon = ({ hasItem }) => {
  const iconString = hasItem ? 'heart-filled' : 'heart'
  return <Icon id={iconString} className={'z-30 min-h-6 min-w-6'} />
}

const WishListBackDrop = ({ handleOpen }) => {
  return (
    <div
      onClick={handleOpen}
      data-modal-parent="wishlist"
      data-modal-role="close"
      class="fixed inset-0 z-40 block cursor-pointer bg-black opacity-60"
    ></div>
  )
}

const parseVariantData = (variants) => {
  if (!variants.length) return []
  const options = variants?.reduce((acc, v) => {
    const attributes = v.metafields?.reduce((bcc, m) => {
      return {
        ...bcc,
        [m.key]: m.value
      }
    }, {})

    const productDataWithoutWidths = {
      id: v.id,
      name: v.title,
      age_group: attributes?.age_group,
      available: v.inventory_quantity > 0,
      uk: attributes?.kids_uk,
      us: attributes?.kids_us,
      eu: attributes?.euro,
      cm: attributes?.centimeter
    }

    if (v?.option2?.length) {
      const productDataWithWidths = {
        ...productDataWithoutWidths,
        width: v.option2
      }
      acc.push(productDataWithWidths)
      return acc
    }

    acc.push(productDataWithoutWidths)
    return acc
  }, [])

  return options
}

const getAllWidthAttribute = (variants) => {
  const widths = new Set(variants?.map((v) => v?.option2).filter((v) => v))
  const widthsList = Array.from(widths?.values()).join(',')
  return widthsList
}

const WishListButton = ({ config }) => {
  const buttonConfig = useMemo(() => {
    switch (config.state) {
      case VIEW_STATES.SELECT: {
        return {
          type: 'button',
          message: 'Select size',
          class: 'btn btn-outline-black btn-md w-full justify-center btn-inline py-2'
        }
      }
      case VIEW_STATES.NOTIFY_ME: {
        return {
          type: 'submit',
          message: `Notify me when ${config.unavailable_stock} is back`,
          class: 'btn btn-solid-black w-full btn-md justify-center btn-inline py-2'
        }
      }
      case VIEW_STATES.ADD_TO_WISHLIST: {
        return {
          type: 'submit',
          message: GM_STATE?.customer?.logged ? 'Next' : 'Add to wishlist',
          class: 'btn btn-solid-black w-full btn-md justify-center btn-inline py-2'
        }
      }
    }
  }, [config])
  return (
    <div className={'flex flex-col gap-4 px-6 py-4'}>
      {config.state === VIEW_STATES.NOTIFY_ME && <NotifyCustomerKlaviyo config={config} />}
      <button className={buttonConfig.class} type={buttonConfig.type}>
        {buttonConfig.message}
      </button>
    </div>
  )
}

//  Dev note: Not currently used, but will require some work if we want to use it
function ProductWishlistQuickSearch({ wishlist, wsCallbacks, result }) {
  const hasItem = wishlist.includes(result.uid)

  const addFirstAvailableVariant = () => {
    const variants = result?.variants
    if (!variants.length) return
    const productId = result?.uid
    const firstVariant = variants[0]?.id
    const key = `${productId}:${firstVariant}`
    wsCallbacks.toggleItem(key)
  }

  return (
    <div className={'absolute right-4 top-4'}>
      <button onClick={addFirstAvailableVariant}>
        <WsIcon hasItem={hasItem} />
      </button>
    </div>
  )
}

function ProductWishlistModal({ wishlist, wsCallbacks, result }) {
  const { omneoWishlists, offlineWishlistItems } = wishlist || {}

  const { client } = useOmneoClient()

  const [isLoading, setIsLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const handleOpen = () => setOpen((o) => !o)
  const [config, setConfig] = useState({
    state: VIEW_STATES.SELECT,
    variantId: undefined,
    unavailable_stock: undefined,
    img: result.imageUrl,
    notifiedVariants: new Set(),
    handleNextView: false,
    messages: {
      success: GM_STATE.apps.klaviyo.backInStock.onSuccessMessage,
      error: ''
    }
  })

  const hasItem = useMemo(() => {
    if (GM_STATE?.customer?.logged) {
      return omneoWishlists?.some((w) => w.items.some((i) => String(i.meta?.productId) === String(result.uid)))
    } else {
      return offlineWishlistItems?.includes(result.uid) ?? false
    }
  }, [omneoWishlists, offlineWishlistItems, result.uid])

  const clearErrors = () => {
    setConfig((c) => ({
      ...c,
      messages: {
        ...c.messages,
        error: ''
      }
    }))
  }

  const variantKeys = useMemo(() => {
    const data = result.variants?.reduce((acc, variant) => ({ ...acc, [variant.id]: variant.sku }), {})

    return data
  }, [])

  const variantData = useMemo(() => {
    return parseVariantData(result.variants)
  }, [])

  const widthData = useMemo(() => {
    return getAllWidthAttribute(result.variants) ?? ''
  }, [])

  const onSubmitCallback = useCallback(
    async (e) => {
      e.preventDefault()
      clearErrors()

      if (!config.variantId) return

      const form = e.currentTarget
      const data = new FormData(form)
      if (config.state === VIEW_STATES.NOTIFY_ME) {
        const customerEmail = data.get('customer_email')
        const validEmailAddress = emailIsValid(customerEmail)

        if (!validEmailAddress.success) {
          setConfig((c) => ({
            ...c,
            messages: {
              ...c.messages,
              error: validEmailAddress.message
            }
          }))
          return
        }

        const response = await handleSubscription({
          customerEmail,
          klaviyoCompanyId: GM_STATE.apps.klaviyo.companyId,
          unavailableStockVariant: config.variantId
        })

        if (response.ok) {
          const set = new Set(config.notifiedVariants)
          set.add(config.variantId)
          setConfig((c) => ({
            ...c,
            notifiedVariants: set
          }))
          return
        }

        const errorMessage = await handleSubscriptionError(response)
        setConfig((c) => ({
          ...c,
          messages: {
            ...c.messages,
            error: errorMessage
          }
        }))

        return
      }

      /**
       * If we are using logged in customer let them selected which wishlist they
       * would like to pick
       */
      if (data.get('omneo_customer')) {
        setConfig((c) => ({ ...c, handleNextView: true }))
        return
      }

      wsCallbacks.setWishlistItem(`${result.uid}:${config.variantId}`)
      pushWishlistSaveEvent({ result, variantId: config.variantId })
      handleOpen()
    },
    [config]
  )

  const handleIconClick = useCallback(async () => {
    if (hasItem) {
      if (!GM_STATE?.customer?.logged) {
        wsCallbacks.removeItemByParent(result.uid)
      } else {
        setIsLoading(true)
        await removeProductFromAllWishlists(client, omneoWishlists, result.uid)
        const wishlistActions = omneoWishlists.reduce((acc, wishlist) => {
          const item = wishlist.items.find((item) => String(item.meta?.productId) === String(result.uid))
          if (item) {
            acc.push({
              action: 'remove',
              list: wishlist,
              item
            })
          }
          return acc
        }, [])
        document.dispatchEvent(
          new CustomEvent('omneo:wishlist:update', { detail: { useCache: false, wishlistActions } })
        )
        setIsLoading(false)
      }

      return
    }

    handleOpen()
  }, [hasItem, result.uid, config.variantId, client, omneoWishlists])

  if (!open) {
    return (
      <div className={'absolute right-4 top-4'}>
        <button onClick={handleIconClick} disabled={isLoading}>
          <WsIcon hasItem={hasItem} />
        </button>
      </div>
    )
  }
  return (
    <>
    <div className={`absolute right-4 top-4 ${open && 'z-max'}`}>

      <button onClick={handleOpen}>
        <WsIcon hasItem={hasItem} />
      </button>
    </div>
    {open && (
      <>
        <div data-ui="visible" className={' group/wishlist fixed inset-0 z-max h-full w-full'}>
          <WishListBackDrop handleOpen={handleOpen} />

          <div
            className={
              'pointer-events-none fixed bottom-0 right-0 top-0 z-50 m-4 flex w-11/12 translate-x-96 flex-col overflow-hidden rounded-xl bg-white opacity-0 transition-all duration-500 group-data-visible/wishlist:pointer-events-auto group-data-visible/wishlist:translate-x-0 group-data-visible/wishlist:opacity-100 md:m-6 md:w-140'
            }
          >
            <button onClick={handleOpen} className={'absolute right-4 top-4 z-50'}>
              <Icon id={'close'} className={'h-6 w-6'} />
            </button>
            {config.handleNextView ? (
              <AccountWishlistModal props={{ wishlist, config, result, variantKeys, handleOpen }} />
            ) : (
              <form
                onSubmit={onSubmitCallback}
                onClick={(e) => {
                  const form = e.currentTarget
                  const data = new FormData(form)
                  const variant = data.get('id')

                  if (!variant) return

                  const unavailableStock = data.get('unavailable_stock')
                  if (unavailableStock && GM_STATE?.apps?.klaviyo?.backInStock?.enabled) {
                    setConfig((c) => ({
                      ...c,
                      unavailableStock,
                      variantId: variant,
                      state: VIEW_STATES.NOTIFY_ME
                    }))
                    return
                  }
                  setConfig((c) => ({
                    ...c,
                    unavailable_stock: undefined,
                    variantId: variant,
                    state: VIEW_STATES.ADD_TO_WISHLIST
                  }))
                }}
              >
                {GM_STATE?.customer?.logged && <input name="omneo_customer" type="hidden" value={'1'} />}
                <x-product-selector
                  class="breathe block min-h-10 w-full scroll-m-80"
                  data-type="UK"
                  data-width={widthData}
                  data-product={result.uid}
                  data-options={JSON.stringify(variantData)}
                ></x-product-selector>
                {/* {GM_STATE?.customer?.logged && (
                  <div>
                    <AccountKidsSizeSuggestion useWindowCache={true} />
                  </div>
                )} */}
                <WishListButton config={config} />
              </form>
            )}
          </div>
        </div>
      </>
    )}
    </>
  )
}

export function ProductWishlist({ wishlist, wsCallbacks, result, type }) {
  switch (type) {
    case 'none': {
      return <></>
    }
    case 'no-modal': {
      return <ProductWishlistQuickSearch wishlist={wishlist} wsCallbacks={wsCallbacks} result={result} />
    }
    default: {
      return <ProductWishlistModal wishlist={wishlist} wsCallbacks={wsCallbacks} result={result} />
    }
  }
}
